import React from 'react'

import _ from 'underscore'

import Countries from '../../utils/Countries.js'
import LocationPicker from '../map/LocationPicker.js'
import {Card, Colors, IconButton, Spinner} from '../UI/index.js'
import AddressAttachment from './AddressAttachment.js'
import {Check, FormRow, FormStack, Select, Input, TimePicker, formSpacing} from '../UI/forms/index.js'
import guid from '../../../utils/guid.js'
import {normalize} from '@utils/normalize.js'
import {emit} from 'src/socketio/socketEmit.js'
import t from 'src/helpers/Languages.js'

const regPostal = /^[0-9]{4} [A-Z]{2}$/

class Address extends React.Component {
    constructor(props) {
        super(props)

        this.state = {
            suggestions: [],
            suggested: false,
            validation: {}
        }

        this.focus = this.focus.bind(this)
    }

    componentDidUpdate(prevProps) {
        const {address, noAutoComplete} = this.props

        // Only look up the street after the user changed the postal code or number, not when an address is loaded
        // (e.g. an imported parcel), otherwise a correct street is replaced by the street of a mistyped postal code
        if (!noAutoComplete && (prevProps.address.postalCode !== address.postalCode || prevProps.address.nr !== address.nr || prevProps.address.addition !== address.addition)) {
            // A pending lookup is for the previous postal code, so its response must not overwrite the new address
            // (e.g. an address chosen from the address book)
            this.lastCallbackId = ''

            if (this.userEditedPostalCode) {
                this.userEditedPostalCode = false
                this.autocomplete(address)
            }
        }
    }

    setName(event) {
        let {validation, suggested} = this.state
        validation.name = null
        this.setState({validation})

        const {addressBook = []} = this.props
        const address = JSON.parse(JSON.stringify(this.props.address))

        const suggestions = event.target.value.length < 1 ?
            [] :
            addressBook.filter((value) => {
                return normalize(value.name.toLowerCase()).indexOf(event.target.value.toLowerCase()) > -1
            })

        address.name = event.target.value.length === 1 ? event.target.value.toUpperCase() : event.target.value

        const suggestion = _.find(suggestions, (suggestion) => {
            return suggestion.name === event.target.value
        })

        if (suggestion) {
            suggested = true
            address.attention = suggestion.attention || ''
            address.street = suggestion.street || ''
            address.nr = suggestion.nr || ''
            address.addition = suggestion.addition || ''
            address.street2 = suggestion.street2 || ''
            address.postalCode = suggestion.postalCode || ''
            address.city = suggestion.city || ''
            address.country = suggestion.country || 'NL'
            address.isBusiness = !!suggestion.isBusiness
            address.email = suggestion.email || address.email || ''
            address.phone = suggestion.phone || address.phone || ''
            address.instructions = suggestion.instructions || address.instructions || ''

            // TODO: remove this after Px pilot
            address.px = suggestion.px || false

            if (!address.overridePosition) {
                if (suggestion.position) {
                    address.position = suggestion.position
                } else {
                    delete address.position
                }
            }

            this.userEditedPostalCode = !suggestion.position

            typeof this.props.onConfirm === 'function' && this.props.onConfirm()
        } else if (suggested) {
            suggested = false
            address.attention = ''
            address.street = ''
            address.nr = ''
            address.addition = ''
            address.street2 = ''
            address.postalCode = ''
            address.city = ''
            address.country = 'NL'
            address.isBusiness = false
            address.email = ''
            address.phone = ''
            address.instructions = ''
            delete address.position
        }

        this.setState({suggestions: suggestions.splice(0, 9), suggested})
        this.change(address)
    }

    setAttention(event) {
        const address = JSON.parse(JSON.stringify(this.props.address))
        address.attention = event.target.value.length === 1 ? event.target.value.toUpperCase() : event.target.value
        this.change(address)
    }

    switchNameAttention() {
        const address = JSON.parse(JSON.stringify(this.props.address))
        const name = address.name
        address.name = address.attention
        address.attention = name
        this.change(address)
    }

    setPostalCode(event) {
        this.userEditedPostalCode = true
        const {validation} = this.state
        validation.postalCode = null
        this.setState({validation})

        const address = JSON.parse(JSON.stringify(this.props.address))
        let postalCode = event.target.value.toUpperCase().replace(/  +/g, ' ')

        if (/^[0-9]{4}[A-Z]{2}$/.test(postalCode)) {
            postalCode = `${postalCode.substr(0, 4)} ${postalCode.substr(4, 2)}`
        }

        if (/^[0-9]{4} [A-Z]{2} $/.test(postalCode)) {
            postalCode = postalCode.trim()
        }

        address.postalCode = postalCode
        !address.overridePosition && delete address.position && delete address.showWarning
        this.change(address)
    }

    setNr(event) {
        this.userEditedPostalCode = true
        const {validation} = this.state
        validation.nr = null
        this.setState({validation})

        const address = JSON.parse(JSON.stringify(this.props.address))
        address.nr = event.target.value.toUpperCase()
        !address.overridePosition && delete address.position && delete address.showWarning
        this.change(address)
    }

    setAddition(event) {
        this.userEditedPostalCode = true
        const address = JSON.parse(JSON.stringify(this.props.address))
        address.addition = event.target.value.toUpperCase()

        if (/[0-9]/.test(address.addition[0])) {
            address.addition = `-${address.addition}`
        } else if (address.addition[1] && /[-]/.test(address.addition[0]) && /[^0-9]/.test(address.addition[1])) {
            address.addition = address.addition.substr(1)
        }

        this.change(address)
    }

    setStreet(event) {
        const {validation} = this.state
        validation.street = null
        this.setState({validation})

        const address = JSON.parse(JSON.stringify(this.props.address))
        address.street = event.target.value.length === 1 ? event.target.value.toUpperCase() : event.target.value

        this.change(address)
    }

    setStreet2(event) {
        const address = JSON.parse(JSON.stringify(this.props.address))
        address.street2 = event.target.value.length === 1 ? event.target.value.toUpperCase() : event.target.value
        this.change(address)
    }

    setCity(event) {
        const {validation} = this.state
        validation.city = null
        this.setState({validation})

        const address = JSON.parse(JSON.stringify(this.props.address))
        address.city = event.target.value.length === 1 ? event.target.value.toUpperCase() : event.target.value
        this.change(address)
    }

    changeState(event) {
        const {validation} = this.state
        validation.state = null
        this.setState({validation})

        const address = JSON.parse(JSON.stringify(this.props.address))
        address.state = event.target.value.length === 1 ? event.target.value.toUpperCase() : event.target.value
        this.change(address)
    }

    setCountry(event) {
        const address = JSON.parse(JSON.stringify(this.props.address))
        address.country = event.target.value
        this.change(address)
    }

    async autocomplete(address) {
        if (address.country === 'NL' && /^[0-9]{4} [A-Z]{2}$/.test(address.postalCode) && address.nr) {
            this.lastCallbackId = guid.generate()

            try {
                const response = await emit('addresses.autocomplete', {address, callbackId: this.lastCallbackId})

                const {data} = response

                const addr = data?.address

                if (data.callbackId !== this.lastCallbackId) {
                    return
                }

                // Get the latest address from props, in case the user has changed it while waiting for the response
                address = structuredClone(this.props.address)

                if (addr) {
                    if (addr.street) {
                        address.street = addr.street || ''
                    }

                    if (addr.city) {
                        address.city = addr.city || ''
                    }

                    address.isBusiness = !!addr.isBusiness

                    if (!address.overridePosition && addr.position && addr.position.lat && addr.position.lng) {
                        address.position = addr.position
                    }

                    if (typeof addr.showWarning !== 'undefined') {
                        address.showWarning = addr.showWarning
                    }
                }

                this.change(address)
                return address
            } catch (err) {
                console.error(err)
            }
        }
    }

    onChangeValue(key, event) {
        const address = JSON.parse(JSON.stringify(this.props.address))
        address[key] = event.target.value
        this.change(address)
    }

    onChangeChecked(key, event) {
        const address = JSON.parse(JSON.stringify(this.props.address))
        address[key] = event.target.checked
        this.change(address)

        if (key === 'overridePosition' && !event.target.checked) {
            this.autocomplete(address)
        }
    }

    change(address) {
        this.props.onChange(address)
    }

    validate(validation = {}) {
        const {id = '', allowEmptyName, state} = this.props
        const address = JSON.parse(JSON.stringify(this.props.address))
        // const validation = {}
        let valid = true
        let focusId = ''

        if (validation.phone) {
            focusId = `phone${id}`
            validation.phone = validation.phone || true
            valid = false
        }

        if (validation.email) {
            focusId = `email${id}`
            validation.email = validation.email || true
            valid = false
        }

        if (validation.state || (state && address.country === 'US' && !address.state)) {
            focusId = `state${id}`
            validation.state = true
            valid = false
        }

        if (validation.city || !address.city) {
            focusId = `city${id}`
            validation.city = true
            valid = false
        }

        if (validation.street || !address.street) {
            focusId = `street${id}`
            validation.street = true
            valid = false
        }

        if (validation.nr || (address.country === 'NL' && !address.nr)) {
            focusId = `nr${id}`
            validation.nr = true
            valid = false
        }

        if (validation.postalCode || !address.postalCode || (address.country === 'NL' && !regPostal.test(address.postalCode))) {
            focusId = `postalCode${id}`
            validation.postalCode = true
            valid = false
        }

        if (validation.name || (!address.name && !allowEmptyName)) {
            focusId = `name${id}`
            validation.name = true
            valid = false
        }

        if (!valid) {
            this.focus(focusId)
        }

        this.setState({validation})
        return valid
    }

    focus(elementId = 'name') {
        const {id = ''} = this.props
        const element = document.getElementById(`${elementId}${id}`)

        if (element) {
            element.focus()
        }
    }

    render() {
        const {suggestions, validation} = this.state
        // nameReadOnly: only the name can't be changed, e.g. own details where an administrator manages the name
        const {id = '', address, noTAV, noCountry, state, email, emailLabel, phone, phoneLabel, businessCheckbox, overridePosition, allowAttachments, showInstructions, showTimeslot, saveCheckbox, switchNameAttention, disabled, readOnly, nameReadOnly, loading} = this.props
        const showSaveCheckbox = saveCheckbox && !disabled && !readOnly

        const countries = [
            {value: 'NL', title: 'Nederland', bold: true},
            {value: 'BE', title: 'België', bold: true},
            {value: 'DE', title: 'Duitsland', bold: true},
            {value: 'FR', title: 'Frankrijk', bold: true},
            {value: 'IT', title: 'Italië', bold: true},
            {value: 'ES', title: 'Spanje', bold: true},
            {value: 'GB', title: 'Verenigd Koninkrijk', bold: true},
            {value: 'US', title: 'Verenigde Staten', bold: true}
        ]

        {Countries.map((country) => {
            countries.push({value: country.code2, title: country.name})
        })}

        return (
            <Card style={{background: Colors.grey20, position: 'relative', padding: formSpacing.inset}}>
                {loading &&
                    <div style={{position: 'absolute', background: 'rgba(255, 255, 255, 0.6)', zIndex: 999, top: 0, left: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: 4, width: '100%', height: '100%'}}>
                        <Spinner />
                    </div>
                }
                <FormStack>
                    <FormRow style={{flexWrap: 'wrap'}}>
                        <Input
                            id={`name${id}`}
                            style={{minWidth: 200}}
                            label='Naam'
                            disabled={disabled}
                            readOnly={readOnly || nameReadOnly}
                            suggestions={nameReadOnly ? undefined : _.pluck(suggestions, 'name')}
                            value={address.name}
                            isInvalid={validation.name}
                            onChange={this.setName.bind(this)}
                            autoComplete='off'
                        />

                        {switchNameAttention && !(disabled || readOnly) &&
                            <IconButton
                                icon='mdi mdi-swap-horizontal'
                                onClick={() => this.switchNameAttention()}
                            />
                        }

                        {!noTAV &&
                            <Input
                                style={{minWidth: 200}}
                                label='Ter attentie van'
                                value={address.attention}
                                disabled={disabled}
                                readOnly={readOnly}
                                onChange={this.setAttention.bind(this)}
                            />
                        }
                    </FormRow>

                    <FormRow style={{flexWrap: 'wrap'}}>
                        <Input
                            id={`postalCode${id}`}
                            style={{minWidth: 100}}
                            label='Postcode'
                            info={address.postalCode && address.country === 'NL' && address.showWarning && 'Dit adres staat niet in onze database, controleer of dit adres geldig is.'}
                            infoIcon='mdi mdi-map-marker-alert'
                            value={address.postalCode}
                            disabled={disabled}
                            readOnly={readOnly}
                            isInvalid={validation.postalCode}
                            onChange={this.setPostalCode.bind(this)}
                        />

                        <FormRow style={{flex: 1, minWidth: 212}}>
                            <Input
                                id={`nr${id}`}
                                style={{minWidth: 100}}
                                label='Huisnummer'
                                value={address.nr}
                                disabled={disabled}
                                readOnly={readOnly}
                                isInvalid={validation.nr}
                                onChange={this.setNr.bind(this)}
                            />

                            <Input
                                style={{minWidth: 100}}
                                label='Toevoeging'
                                value={address.addition}
                                disabled={disabled}
                                readOnly={readOnly}
                                onChange={this.setAddition.bind(this)}
                            />
                        </FormRow>
                    </FormRow>

                    <FormRow style={{flexWrap: 'wrap'}}>
                        <Input
                            id={`street${id}`}
                            style={{minWidth: 200}}
                            label='Straat'
                            value={address.street || ''}
                            disabled={disabled}
                            readOnly={readOnly}
                            isInvalid={validation.street}
                            onChange={this.setStreet.bind(this)}
                        />
                        <Input
                            style={{minWidth: 200}}
                            label='2e adres regel'
                            value={address.street2}
                            disabled={disabled}
                            readOnly={readOnly}
                            onChange={this.setStreet2.bind(this)}
                        />
                    </FormRow>

                    <FormRow style={{flexWrap: 'wrap'}}>
                        <Input
                            id={`city${id}`}
                            style={{minWidth: 200}}
                            label='Plaats'
                            value={address.city}
                            disabled={disabled}
                            readOnly={readOnly}
                            isInvalid={validation.city}
                            onChange={this.setCity.bind(this)}
                        />

                        <Select
                            style={{minWidth: 200}}
                            label='Land'
                            disabled={noCountry || disabled}
                            readOnly={readOnly}
                            value={noCountry ? (address.country || 'NL') : address.country}
                            options={countries}
                            onChange={this.setCountry.bind(this)}
                        />
                    </FormRow>

                    {state && address.country === 'US' &&
                        <FormRow>
                            <Input
                                id={`state${id}`}
                                style={{minWidth: 200}}
                                label='Staat'
                                value={address.state}
                                disabled={disabled}
                                readOnly={readOnly}
                                isInvalid={validation.state}
                                onChange={this.changeState.bind(this)}
                            />
                            <div style={{flex: 1}} />
                        </FormRow>
                    }

                    {(email || phone) &&
                        <FormRow style={{flexWrap: 'wrap'}}>
                            {email &&
                                <Input
                                    id={`email${id}`}
                                    style={{minWidth: 200}}
                                    label={emailLabel || 'Emailadres'}
                                    value={address.email}
                                    disabled={disabled}
                                    readOnly={readOnly}
                                    isInvalid={validation.email}
                                    onChange={this.onChangeValue.bind(this, 'email')}
                                />
                            }

                            {phone &&
                                <Input
                                    id={`phone${id}`}
                                    style={{minWidth: 200}}
                                    label={phoneLabel || 'Telefoon'}
                                    value={address.phone}
                                    disabled={disabled}
                                    readOnly={readOnly}
                                    isInvalid={validation.phone}
                                    onChange={this.onChangeValue.bind(this, 'phone')}
                                />
                            }
                        </FormRow>
                    }

                    {(showInstructions || showTimeslot) &&
                        <FormRow style={{flexWrap: 'wrap'}}>
                            {showInstructions &&
                                <Input
                                    id={`instructions${id}`}
                                    style={{minWidth: 200}}
                                    label={t('instructions')}
                                    value={address.instructions}
                                    disabled={disabled}
                                    readOnly={readOnly}
                                    onChange={this.onChangeValue.bind(this, 'instructions')}
                                />
                            }

                            {showTimeslot &&
                                <FormRow style={{flex: 1, minWidth: 200}}>
                                    <TimePicker
                                        label={t('startTime')}
                                        info={t('addressBookTimeslotInfo')}
                                        value={address.startTime || ''}
                                        allowEmpty
                                        disabled={disabled}
                                        readOnly={readOnly}
                                        onChange={this.onChangeValue.bind(this, 'startTime')}
                                    />

                                    <TimePicker
                                        label={t('endTime')}
                                        value={address.endTime || ''}
                                        allowEmpty
                                        disabled={disabled}
                                        readOnly={readOnly}
                                        onChange={this.onChangeValue.bind(this, 'endTime')}
                                    />
                                </FormRow>
                            }
                        </FormRow>
                    }

                    {(businessCheckbox || overridePosition || showSaveCheckbox || allowAttachments) &&
                        <FormRow style={{flexWrap: 'wrap'}}>
                            {businessCheckbox &&
                                <Check
                                    style={{minWidth: 200}}
                                    label='Bedrijfsadres'
                                    checked={address.isBusiness}
                                    disabled={disabled}
                                    readOnly={readOnly}
                                    onChange={this.onChangeChecked.bind(this, 'isBusiness')}
                                />
                            }

                            {overridePosition &&
                                <Check
                                    style={{minWidth: 200}}
                                    label='Aangepaste locatie'
                                    checked={address.overridePosition}
                                    disabled={disabled}
                                    readOnly={readOnly}
                                    onChange={this.onChangeChecked.bind(this, 'overridePosition')}
                                />
                            }

                            {showSaveCheckbox &&
                                <Check
                                    style={{minWidth: 200}}
                                    label='Opslaan in adresboek'
                                    checked={address.save}
                                    onChange={this.onChangeChecked.bind(this, 'save')}
                                />
                            }

                            {allowAttachments &&
                                <div style={{flex: 1}}>
                                    <AddressAttachment
                                        edit
                                        onChange={this.change.bind(this)}
                                        address={address}
                                    />
                                </div>
                            }
                        </FormRow>
                    }
                </FormStack>

                {address.overridePosition &&
                    <LocationPicker
                        style={{marginTop: 10}}
                        value={address?.position}
                        onChange={(position) => {
                            this.change({
                                ...address,
                                position
                            })
                        }}
                    />
                }
            </Card>
        )
    }
}

export default Address
